import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Notice } from '../types';

interface NoticeContextType {
  notices: Notice[];
  loading: boolean;
  error: string | null;
  addNotice: (notice: Omit<Notice, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateNotice: (id: number, notice: Partial<Notice>) => Promise<void>;
  deleteNotice: (id: number) => Promise<void>;
  getNotice: (id: number) => Notice | undefined;
  incrementViews: (id: number) => Promise<void>;
  refreshNotices: () => Promise<void>;
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const NoticeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 공지사항 데이터 가져오기
  const fetchNotices = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('notices')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      console.log('📥 Fetched notices from DB:', data);

      // 공지글 정렬 로직:
      // 1. is_important가 true이고 notice_end_date가 미래 또는 null인 경우 → 상단 고정
      // 2. 나머지는 created_at 기준 최신순
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const sortedNotices = (data || []).sort((a, b) => {
        const aIsActive = a.is_important && (!a.notice_end_date || new Date(a.notice_end_date) >= now);
        const bIsActive = b.is_important && (!b.notice_end_date || new Date(b.notice_end_date) >= now);

        // 둘 다 활성 공지 또는 둘 다 일반 글인 경우: created_at 기준 정렬
        if (aIsActive === bIsActive) {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }

        // 활성 공지가 상단에 오도록
        return aIsActive ? -1 : 1;
      });

      setNotices(sortedNotices);
    } catch (err) {
      console.error('Error fetching notices:', err);
      setError(err instanceof Error ? err.message : '공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchNotices();
  }, []);

  const addNotice = async (noticeData: Omit<Notice, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);

      const { error: insertError } = await supabase
        .from('notices')
        .insert({
          title: noticeData.title,
          department: noticeData.department,
          content: noticeData.content,
          is_important: noticeData.is_important || false,
          notice_end_date: noticeData.notice_end_date || null,
          is_published: noticeData.is_published ?? true,
          author: noticeData.author || '관리자',
          view_count: 0
        });

      if (insertError) throw insertError;

      // 데이터 다시 불러오기
      await fetchNotices();
    } catch (err) {
      console.error('Error adding notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateNotice = async (id: number, noticeData: Partial<Notice>) => {
    try {
      setError(null);

      const updateData: any = {};
      if (noticeData.title) updateData.title = noticeData.title;
      if (noticeData.department) updateData.department = noticeData.department;
      if (noticeData.content) updateData.content = noticeData.content;
      if (noticeData.is_important !== undefined) updateData.is_important = noticeData.is_important;
      if (noticeData.notice_end_date !== undefined) updateData.notice_end_date = noticeData.notice_end_date;
      if (noticeData.is_published !== undefined) updateData.is_published = noticeData.is_published;
      if (noticeData.author) updateData.author = noticeData.author;

      console.log('🔍 Update Notice - noticeData:', noticeData);
      console.log('🔍 Update Notice - updateData:', updateData);

      const { error: updateError } = await supabase
        .from('notices')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      // 데이터 다시 불러오기
      await fetchNotices();
    } catch (err) {
      console.error('Error updating notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteNotice = async (id: number) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('notices')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // 로컬 상태에서 제거
      setNotices(prev => prev.filter(notice => notice.id !== id));
    } catch (err) {
      console.error('Error deleting notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 삭제에 실패했습니다.');
      throw err;
    }
  };

  const incrementViews = async (id: number) => {
    try {
      // view_count를 직접 증가
      const notice = notices.find(n => n.id === id);
      if (notice) {
        const { error: updateError } = await supabase
          .from('notices')
          .update({ view_count: notice.view_count + 1 })
          .eq('id', id);

        if (updateError) throw updateError;

        // 로컬 상태 업데이트
        setNotices(prev => prev.map(n =>
          n.id === id ? { ...n, view_count: n.view_count + 1 } : n
        ));
      }
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const getNotice = (id: number) => {
    return notices.find(notice => notice.id === id);
  };

  const refreshNotices = async () => {
    await fetchNotices();
  };

  return (
    <NoticeContext.Provider value={{
      notices,
      loading,
      error,
      addNotice,
      updateNotice,
      deleteNotice,
      getNotice,
      incrementViews,
      refreshNotices
    }}>
      {children}
    </NoticeContext.Provider>
  );
};

export const useNotices = () => {
  const context = useContext(NoticeContext);
  if (!context) {
    throw new Error('useNotices must be used within a NoticeProvider');
  }
  return context;
};
